import './ReferralOwnershipModal.scss';
import React, { useEffect, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Close } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from 'v3/Components/Shared/Buttons';
import { useApiWorker } from 'Utilities/CommonHooks';
import { Entity, useEntityRefresh } from 'Context/EntityProvider';
import { useToasts } from 'Context/ToastProvider';
import { User } from 'Models/SharedModels';

const STALE_CLAIM_CODE = 'PHASE_ADVANCED';
const STALE_CLAIM_MESSAGE =
  'This case has already moved to the next phase. Your claim was not saved.';

export const DENIED_OWNER_SESSION_KEY = 'deniedOwnerRefId';

export const useOwnershipModal = (
  entity: Entity | null | undefined,
  userdata: User | null | undefined,
  id: string
) => {
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerInfo, setBannerInfo] = useState({ isOwner: false, ownerName: '' });
  const [hasInteracted, setHasInteracted] = useState(false);

  const isInternalUser = userdata?.isInternal || userdata?.isAdmin;

  useEffect(() => {
    if (!entity || !isInternalUser) return;

    const isCurrentUserOwner = entity.isCurrentUserOwner || false;
    const ownerName = entity.ownerName || '';

    if (hasInteracted) {
      if (isCurrentUserOwner && !showBanner) {
        setBannerInfo({ isOwner: true, ownerName: 'You' });
        setShowBanner(true);
        setShowModal(false);
      }
      return;
    }

    if (isCurrentUserOwner) {
      setBannerInfo({ isOwner: true, ownerName: 'You' });
      setShowBanner(true);
      setShowModal(false);
      return;
    }

    const isDenied = sessionStorage.getItem(DENIED_OWNER_SESSION_KEY) === id;
    if (isDenied) {
      setHasInteracted(true);
      if (entity.hasOwner && ownerName) {
        setBannerInfo({ isOwner: false, ownerName });
        setShowBanner(true);
      }
      return;
    }

    setShowModal(true);
    setShowBanner(false);
  }, [entity, isInternalUser, hasInteracted, showBanner]);

  useEffect(() => {
    if (showModal) {
      sessionStorage.removeItem(DENIED_OWNER_SESSION_KEY);
    }
  }, [showModal]);

  const handleOwnershipClaimed = (isOwner: boolean, ownerName: string) => {
    setHasInteracted(true);
    setBannerInfo({ isOwner, ownerName });
    setShowBanner(true);
    setShowModal(false);
  };

  const handleModalDismiss = () => {
    setHasInteracted(true);
    setShowModal(false);
    if (entity?.hasOwner && entity?.ownerName) {
      setBannerInfo({ isOwner: false, ownerName: entity.ownerName });
      setShowBanner(true);
    }
  };

  const handleDeny = () => {
    if (isInternalUser && id) {
      sessionStorage.setItem(DENIED_OWNER_SESSION_KEY, id);
    }
  };

  return {
    isInternalUser,
    showModal,
    showBanner,
    bannerInfo,
    handleOwnershipClaimed,
    handleModalDismiss,
    handleDeny,
  };
};

interface ReferralOwnershipModalProps {
  isOpen: boolean;
  toggle: () => void;
  entityId: string;
  hasExistingOwner: boolean;
  existingOwnerName?: string;
  onOwnershipClaimed: (isOwner: boolean, ownerName: string) => void;
  onDeny?: () => void;
  apiEndpoint?: string;
  entityIdField?: string;
  entityLabel?: string;
  /**
   * The case's current phase (Intake/Assigning/QA), echoed back on claim so the server can detect a
   * stale claim submitted after the case has already advanced. Only meaningful for Referrals — a
   * ReferralsRequest only ever represents the Intake phase, so it's omitted there.
   */
  phase?: string;
  /**
   * The Id of the phase-owner checkpoint last seen by the client, echoed back on claim so the server can
   * detect a stale claim raced by a newer checkpoint (an automatic sign-off or someone else's claim)
   * written after the client last read the case, even within the same phase. Only meaningful for Referrals.
   */
  checkpointId?: string | null;
}

export const ReferralOwnershipModal: React.FC<ReferralOwnershipModalProps> = ({
  isOpen,
  toggle,
  entityId,
  hasExistingOwner,
  existingOwnerName,
  onOwnershipClaimed,
  onDeny,
  apiEndpoint = 'referrals/ClaimOwnership',
  entityIdField = 'referralId',
  entityLabel = 'referral',
  phase,
  checkpointId,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API = useApiWorker();
  const toast = useToasts();
  const refreshEntity = useEntityRefresh();

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await API.post(apiEndpoint, {
        [entityIdField]: entityId,
        overrideExisting: hasExistingOwner,
        ...(phase ? { phase } : {}),
        ...(checkpointId ? { checkpointId } : {}),
      });

      if (response.status === 200) {
        onOwnershipClaimed(true, 'You');
      }
    } catch (error: any) {
      if (error?.response?.status === 409 && error?.response?.data?.code === STALE_CLAIM_CODE) {
        toast.error(error.response.data.message || STALE_CLAIM_MESSAGE);
        toggle();
        refreshEntity();
      } else {
        console.error('[Modal] Error claiming ownership:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasExistingOwner) {
      onOwnershipClaimed(false, existingOwnerName || '');
    }
    toggle();
  };

  const handleDeny = () => {
    onDeny?.();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog className="referral-ownership-dialog" onClose={handleClose}>
      <div className="dialog-header">
        <div className="dialog-title">Claim Ownership</div>
        <div className="dialog-close" onClick={handleClose}>
          <span className="dialog-close-icon">
            <Close />
          </span>
        </div>
      </div>
      <div className="dialog-row">
        <div className="dialog-column">
          {hasExistingOwner && (
            <div className="ownership-info-banner">
              <i className="k-icon k-i-information"></i>
              <span>
                It looks like {existingOwnerName} has ownership of this{' '}
                {entityLabel}.
              </span>
            </div>
          )}
          <p>
            Do you want to claim ownership over this {entityLabel}?
            {hasExistingOwner &&
              ` By claiming this ${entityLabel}, you will override the current ownership.`}
          </p>
        </div>
      </div>
      <div className="dialog-footer">
        <SecondaryButton
          className="centered"
          onClick={handleDeny}
          disabled={isSubmitting}
        >
          <div className="title">Deny</div>
        </SecondaryButton>
        <PrimaryButton
          className={`centered ${isSubmitting ? 'disabled' : ''}`}
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          <div className="title">
            {isSubmitting ? 'Taking Ownership...' : 'Claim'}
          </div>
        </PrimaryButton>
      </div>
    </Dialog>
  );
};
